/**
 * Types et utilitaires partagés par la salle d'audience (/tribunal/:id).
 */
import { getCurrentLanguage, getDateLocale } from '@/i18n';

export type Side = 'prosecution' | 'defense';

export interface Witness {
  id: string;
  name: string;
  role: string;
  side: 'prosecution' | 'defense' | 'neutral';
  testimony_summary?: string;
  personality?: string;
}

export interface Evidence {
  id: string;
  title: string;
  description: string;
  available_to: 'both' | 'prosecution' | 'defense';
}

export interface GeneratedCase {
  title: string;
  headline: string;
  case_type: string;
  accusation: string;
  charges: string[];
  defendant: { name: string; age: number; profession: string; background: string };
  facts: string;
  context: string;
  key_points_prosecution: string[];
  key_points_defense: string[];
  witnesses: Witness[];
  available_evidence: Evidence[];
  judge_instructions?: string;
  /** Grand procès reconstitué (OJ Simpson…) : vrai verdict révélé après la partie. */
  famous?: { slug: string; date: string; label: string; disclaimer: string; court?: string; real_outcome?: string; real_verdict?: string; sources?: string[] };
}

export interface TrialMessage {
  id: string;
  role: 'judge' | 'clerk' | 'prosecution' | 'defense' | 'witness' | 'system';
  speaker_name: string;
  content: string;
  phase: string;
  msg_type: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface EvidenceItem {
  id: string;
  submitted_by: string;
  title: string;
  description: string;
  admitted: boolean | null;
  created_at: string;
}

export interface Verdict {
  verdict: string;
  motivation: string;
  winner?: 'prosecution' | 'defense' | 'draw';
  appreciation?: string;
  scores?: Record<string, number>;
  sentence?: string;
}

export interface Trial {
  id: string;
  title: string;
  headline: string;
  case_type: string;
  /** Juridiction (marché) : 'fr' ou 'eu'. */
  jurisdiction?: string;
  difficulty: string;
  status: 'open' | 'in_progress' | 'completed';
  generated_case: GeneratedCase;
  /** Sièges pris (le serveur n'expose jamais les UID des avocats). */
  prosecution_taken: boolean;
  defense_taken: boolean;
  /** Rôle du demandeur, calculé côté serveur à partir du jeton Firebase. */
  viewer_role?: Side | null;
  /** Côté client : la réponse a été obtenue avec un jeton (viewer_role fiable). */
  _authed?: boolean;
  prosecution_name: string | null;
  defense_name: string | null;
  current_phase: string;
  current_speaker: string | null;
  /** Intervenant IA en train de rédiger (appel LLM en cours côté serveur). */
  pending_actor?: 'judge' | 'witness' | null;
  verdict: Verdict | null;
  scores: Record<string, number> | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  messages: TrialMessage[];
  evidence_list: EvidenceItem[];
  /** Progression de la phase en cours (interventions requises par partie). */
  phase_progress?: { phase: string; required: number; prosecution: number; defense: number } | null;
}

/** Étapes de l'audience, dans l'ordre (sans « waiting »). */
export const DEBATE_PHASES = ['opening', 'examination', 'evidence', 'closing', 'deliberation', 'verdict'];

/** Couleurs des camps (texte AA sur fond clair). */
export const SIDE_TEXT: Record<string, string> = {
  prosecution: 'text-[#9b3a2e] dark:text-[#c47a68]',
  defense:     'text-[#3d5a73] dark:text-[#8aa3b8]',
  neutral:     'text-stone-600 dark:text-stone-300',
};

/** Ajoute `lang=<langue courante>` à une URL /api/trials… (le backend choisit la langue des agents IA). */
export function withLang(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}lang=${getCurrentLanguage()}`;
}

export function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString(getDateLocale(), { hour: '2-digit', minute: '2-digit' });
}

/** Échappe le HTML brut avant toute injection dans le DOM. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Rend le contenu d'un message d'audience : texte échappé (ce contenu est
 * saisi librement par des avocats et lu par des visiteurs non authentifiés
 * via /poll — jamais de HTML brut), puis **gras** et [libellé](url) en
 * liens réels, restreints à http(s) ou à un chemin interne (/...).
 */
export function renderContent(content: string): string {
  let safe = escapeHtml(content);
  safe = safe.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Chemin interne : « / » suivi d'un caractère ordinaire (ni « / » ni « \ »,
  // que les navigateurs lisent comme « / » : « /\site.com » sortirait du site).
  safe = safe.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)\\]+|\/[A-Za-z0-9][^\s)\\]*)\)/g,
    (_m, label, url) => {
      const external = /^https?:\/\//.test(url);
      return `<a href="${url}" class="text-[#3d5a73] dark:text-[#8aa3b8] underline decoration-dotted hover:decoration-solid"${external ? ' target="_blank" rel="noopener noreferrer"' : ''}>${label}</a>`;
    },
  );
  return safe;
}

/** Texte brut (markdown et liens retirés), tronqué à `max` caractères. */
export function plainExcerpt(s: string, max: number): string {
  const plain = s
    .replace(/\*\*|__|`|#+\s/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  return plain.length > max ? plain.slice(0, max - 1).trimEnd() + '…' : plain;
}

/** Retire le préfixe rituel « La Cour : » / « Le Greffier : » des agents IA. */
export function stripSpeakerPrefix(s: string): string {
  return s.replace(/^\s*(La Cour|Le Greffier|The Court|The Clerk|Das Gericht|Der Gerichtsschreiber)\s*:\s*/i, '');
}

/** Lecture/écriture localStorage tolérantes (navigation privée, stockage bloqué). */
export function readLocal(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
export function writeLocal(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* stockage indisponible */ }
}

/** Signal d'abandon après `ms` (AbortSignal.timeout n'existe qu'à partir de Safari 16). */
export function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms);
  const c = new AbortController();
  setTimeout(() => c.abort(), ms);
  return c.signal;
}
